/**
 * File: breadcrumb_navegation.tsx
 * Description: Reusable breadcrumb navigation component that renders a home icon link followed
 * by a configurable list of labelled navigation links, using MUI Breadcrumbs and React Router.
 */
import {
  Typography,
  useTheme,
  Link,
  Breadcrumbs,
} from "@mui/material";
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";
import HomeIcon from '@mui/icons-material/Home';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Props for the BreadcrumbNavegation component.
 */
interface BreadcrumbNavegationProps{
    items: {label: string, to: string}[];
}

/**
 * Renders a breadcrumb trail with a home icon as the first crumb followed by
 * the provided navigation items. Link colours adapt to the current MUI theme mode.
 *
 * @param {BreadcrumbNavegationProps} props - Component props.
 * @param {{label: string, to: string}[]} props.items - Ordered list of breadcrumb entries, each with a display label and a route path.
 * @returns {JSX.Element}
 */
function BreadcrumbNavegation({items}: BreadcrumbNavegationProps) {

    // Main App Theme
    const theme = useTheme();
    const colors = ColorsPalletTheme(theme.palette.mode);

    return (
        <Breadcrumbs aria-label="breadcrumb" sx={{padding: {xs:"10px 0px 0px 10px", sm:2}}}>
            <Link
                component={RouterLink}
                to={`/`}
                sx={{color: colors.gray[100], width: 'fit-content'}}
                underline="hover"
                >
                <HomeIcon fontSize="small" />
            </Link>
            {items.map((item) => (
                <Link
                    component={RouterLink}
                    to={item.to}
                    sx={{color: colors.gray[100], width: 'fit-content'}}
                    underline="hover"
                    >
                    <Typography variant="body1">{item.label}</Typography>
                </Link>
            ))}
        </Breadcrumbs>
    );
}

export default BreadcrumbNavegation;
